import { z } from 'zod';

/**
 * Catégorie fermée d'un ticket lead — la question qu'un utilisateur pose sur
 * une fiche. Trois motifs proposés à la création (liste revue en réunion le
 * 05/10/2026) ; le détail est porté par le texte libre du premier message,
 * OBLIGATOIRE (cf. TICKET_BODY_MIN_LENGTH).
 *
 * Les valeurs retirées restent dans l'enum (une valeur d'enum Postgres ne se
 * retire pas) : elles ne sont plus PROPOSÉES à la création
 * — cf. `TICKET_CATEGORIES_SELECTABLE` — mais les tickets existants qui les
 * portent gardent leur libellé.
 */
export const TicketCategory = {
  /** « Je ne comprends pas le statut de cette fiche ». */
  status_unclear: 'status_unclear',
  /** « Mon client a raté l'appel, que faire ? » — réponse affichée d'office (cf. TICKET_MISSED_CALL_STEPS_FR). */
  missed_call: 'missed_call',
  /** @deprecated 05/10/2026 — « contacté maintenant » n'est jamais possible ; remplacé par `missed_call`. */
  contact_now: 'contact_now',
  /** @deprecated 05/10/2026 — la fiche est figée à l'envoi (consentement compris) : rien à en faire. */
  wrong_data: 'wrong_data',
  /** « Autre demande ». */
  other: 'other',
  /** @deprecated MVP — fiche bloquée en transmission. Plus proposé. */
  stuck_in_delivery: 'stuck_in_delivery',
  /** @deprecated MVP — vente conclue non remontée. Plus proposé. */
  sale_not_reflected: 'sale_not_reflected',
} as const;

export type TicketCategory = (typeof TicketCategory)[keyof typeof TicketCategory];

/** Toutes les valeurs, héritées comprises — pour LIRE un ticket. */
export const TicketCategorySchema = z.nativeEnum(TicketCategory);

/**
 * Les motifs proposés à la CRÉATION, dans l'ordre d'affichage du select.
 * Source unique front + back : le schéma de création (`CreateTicketSchema`)
 * refuse tout ce qui n'y est pas.
 */
export const TICKET_CATEGORIES_SELECTABLE = [
  TicketCategory.status_unclear,
  TicketCategory.missed_call,
  TicketCategory.other,
] as const;

export type SelectableTicketCategory = (typeof TICKET_CATEGORIES_SELECTABLE)[number];

export const SelectableTicketCategorySchema = z.enum(TICKET_CATEGORIES_SELECTABLE);

/**
 * Libellés FR affichés côté UI et dans les notifications Slack.
 * Centralisés ici pour éviter une traduction parallèle front/back. Les
 * motifs sont formulés à la première personne : c'est ce que l'utilisateur
 * choisit dans la modale, mot pour mot.
 */
export const TICKET_CATEGORY_LABEL_FR: Record<TicketCategory, string> = {
  status_unclear: 'Je ne comprends pas le statut de cette fiche',
  missed_call: "Mon client a raté l'appel, que faire ?",
  contact_now: 'Je veux que mon client soit contacté maintenant',
  wrong_data: 'Les données de la fiche comportent une erreur',
  other: 'Autre demande',
  stuck_in_delivery: 'Fiche bloquée en transmission',
  sale_not_reflected: 'Vente non remontée',
};

/**
 * Réponse affichée dès que le vendeur choisit « Mon client a raté l'appel » —
 * avant tout envoi : la réponse tient en trois gestes, l'écrire une fois ici
 * évite autant d'appels au support (réunion du 05/10/2026). Le vendeur peut
 * encore envoyer sa question si ça ne suffit pas.
 */
export const TICKET_MISSED_CALL_INTRO_FR =
  'Le centre rappelle votre client plusieurs fois. Pour l’aider à décrocher :';

export const TICKET_MISSED_CALL_STEPS_FR = {
  mirror: 'Donnez-lui le numéro miroir : il peut rappeler lui-même, depuis son mobile.',
  sms: 'Envoyez-lui un SMS depuis la fiche (bouton SMS) : le texte et le numéro sont prêts.',
  call: 'Appelez-le pour lui dire de décrocher au prochain appel.',
} as const;

/**
 * Cycle de vie d'un ticket :
 *   open ──▶ in_progress ──▶ answered ──▶ closed
 *
 * `open` = créé par le vendeur, en attente. `answered` est posé
 * automatiquement quand un admin répond à un ticket `open` (transparence :
 * une réponse vaut prise en charge). `closed` pose `resolvedAt` ; toute
 * réouverture (retour à un statut non-`closed`) l'efface.
 */
export const TicketStatus = {
  open: 'open',
  in_progress: 'in_progress',
  answered: 'answered',
  closed: 'closed',
} as const;

export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

export const TicketStatusSchema = z.nativeEnum(TicketStatus);

/** Libellés FR des statuts de ticket (UI + Slack). */
export const TICKET_STATUS_LABEL_FR: Record<TicketStatus, string> = {
  open: 'Ouvert',
  in_progress: 'Pris en charge',
  answered: 'Répondu',
  closed: 'Clôturé',
};
